// Top-bar app shell.
//
// Single chrome surface: glass top bar with project switcher (left), view
// toggle Graph/Jobs (center), and account avatar (right). No sidebar, no
// nav menu, no global search. The two views own the rest of the viewport.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BarChart3, Check, ChevronDown, Copy, ExternalLink, Info, LogOut, Pin, Plus, Settings, Shield, User } from "lucide-react";
import { Popover } from "@base-ui-components/react/popover";
import { Select } from "@base-ui-components/react/select";
import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router";

import { AskPalette, type PaletteSeed } from "@/components/ask-palette";
import { GlassPanel, Pill, Surface } from "@/components/glass";
import { ImpersonationBanner } from "@/components/impersonation-banner";
import { NewProjectDialog } from "@/components/new-project-dialog";
import { authClient, signOut, useSession } from "@/lib/auth-client";

interface Project {
	id: string;
	name: string;
	organizationId?: string | null;
	githubRepo?: string | null;
	defaultBranch?: string;
	createdAt?: string;
}

interface Organization {
	id: string;
	name: string;
	slug: string;
}

interface UserSettings {
	pinnedProjectIds: string[];
}

interface AppShellProps {
	children: React.ReactNode;
}

async function fetchProjects(): Promise<Project[]> {
	const r = await fetch("/api/projects", { credentials: "include" });
	if (!r.ok) throw new Error(`projects: ${r.status}`);
	const body = (await r.json()) as { projects: Project[] };
	return body.projects;
}

async function fetchSettings(): Promise<UserSettings> {
	const r = await fetch("/api/settings", { credentials: "include" });
	if (!r.ok) throw new Error(`settings: ${r.status}`);
	const body = (await r.json()) as { settings: UserSettings };
	return body.settings;
}

export function AppShell({ children }: AppShellProps) {
	const params = useParams<{ id: string }>();
	const location = useLocation();
	const navigate = useNavigate();
	const session = useSession();

	const projectsQ = useQuery({
		queryKey: ["projects"],
		queryFn: fetchProjects,
		refetchOnWindowFocus: false,
	});
	const settingsQ = useQuery({
		queryKey: ["settings"],
		queryFn: fetchSettings,
		refetchOnWindowFocus: false,
	});

	// Spec 30 PR 2: workspaces are organizations. List them so the
	// switcher can render them; the active org id comes from the
	// session (set via authClient.organization.setActive).
	const orgsQ = useQuery({
		queryKey: ["organizations"],
		queryFn: async (): Promise<Organization[]> => {
			const r = await authClient.organization.list();
			if (r.error) throw new Error(r.error.message ?? "list-orgs failed");
			return (r.data as Organization[]) ?? [];
		},
		refetchOnWindowFocus: false,
	});

	const activeOrganizationId =
		(session.data?.session as { activeOrganizationId?: string | null } | null)
			?.activeOrganizationId ?? null;
	const organizations = orgsQ.data ?? [];
	const activeOrg =
		organizations.find((o) => o.id === activeOrganizationId) ??
		organizations[0] ??
		null;

	// Scope projects to the active org so the switcher only lists
	// what belongs to "this workspace". When the active org changes,
	// the list updates without a refetch — the API returns everything
	// the user can see across orgs.
	const allProjects = projectsQ.data ?? [];
	const projects = activeOrg
		? allProjects.filter((p) => p.organizationId === activeOrg.id)
		: allProjects;
	const pinnedProjectIds = settingsQ.data?.pinnedProjectIds ?? [];
	const pinnedSet = new Set(pinnedProjectIds);
	const current =
		projects.find((p) => p.id === params.id) ??
		projects.find((p) => pinnedSet.has(p.id)) ??
		projects[0] ??
		null;

	// The "no project in the URL → pick first" redirect lives on the
	// Index page (`/`), not here. Doing it in AppShell broke every
	// route that's not project-scoped (e.g. `/admin`, `/login`'s
	// post-auth bounce) because params.id is undefined there and
	// the effect would silently navigate the user away.

	// Which view tab is active (null when on a side route like /settings).
	const view: "graph" | "jobs" | "ask" | null = location.pathname.endsWith(
		"/jobs",
	)
		? "jobs"
		: location.pathname.endsWith("/ask")
			? "ask"
			: location.pathname.endsWith("/settings")
				? null
				: "graph";

	useEffect(() => {
		if (!location.pathname.startsWith("/projects/")) return;
		if (!params.id) return;
		if (current && current.id !== params.id) {
			navigate(`/projects/${current.id}/${view ?? "graph"}`, { replace: true });
			return;
		}
		if (!current && projectsQ.isSuccess && orgsQ.isSuccess) {
			navigate("/account/settings", { replace: true });
		}
	}, [
		current,
		location.pathname,
		navigate,
		orgsQ.isSuccess,
		params.id,
		projectsQ.isSuccess,
		view,
	]);

	// Cmd+K opens the ask palette from anywhere in the app.
	// Also listens for `renketsu:open-palette` CustomEvents so other
	// pages (like the /ask thread view) can seed the palette with a
	// prior thread when the user clicks "Continue the thread".
	const [paletteOpen, setPaletteOpen] = useState(false);
	const [paletteSeed, setPaletteSeed] = useState<PaletteSeed | null>(null);
	useEffect(() => {
		const onKey = (e: KeyboardEvent) => {
			if ((e.metaKey || e.ctrlKey) && e.key === "k") {
				e.preventDefault();
				setPaletteOpen((v) => !v);
				// ⌘K-opened palette is a fresh ask; drop any stale seed.
				if (!paletteOpen) setPaletteSeed(null);
			}
		};
		const onSeed = (e: Event) => {
			const detail = (e as CustomEvent).detail as PaletteSeed | undefined;
			if (!detail) return;
			setPaletteSeed(detail);
			setPaletteOpen(true);
		};
		window.addEventListener("keydown", onKey);
		window.addEventListener("renketsu:open-palette", onSeed);
		return () => {
			window.removeEventListener("keydown", onKey);
			window.removeEventListener("renketsu:open-palette", onSeed);
		};
	}, [paletteOpen]);

	function closePalette() {
		setPaletteOpen(false);
		// Clear the seed AFTER the dialog finishes animating out so
		// the indicator doesn't blink during the close transition.
		setTimeout(() => setPaletteSeed(null), 200);
	}

	return (
		<Surface className="flex flex-col h-screen">
			<ImpersonationBanner />
			<header className="shrink-0 px-4 pt-3 pb-3">
				<GlassPanel
					variant="hi"
					className="flex items-center justify-between px-3 py-2 gap-3"
				>
					{/* Left: brand mark + org switcher / project switcher + info + settings */}
					<div className="flex items-center gap-2 min-w-0">
						<Link to="/" className="flex items-center px-1" aria-label="renketsu">
							<img src="/mark.svg" alt="" className="w-5 h-5" />
						</Link>
						<div className="h-5 w-px bg-[var(--color-border)] mx-1" />
						<OrgSwitcher
							organizations={organizations}
							currentId={activeOrg?.id}
							currentName={activeOrg?.name}
						/>
						<span className="text-[var(--color-text-dim)] text-[12px] select-none">
							/
						</span>
						<ProjectSwitcher
							projects={projects}
							currentId={current?.id}
							currentName={current?.name}
							pinnedProjectIds={pinnedProjectIds}
							onSelect={(id) => navigate(`/projects/${id}/${view ?? "graph"}`)}
						/>
						{current && <ProjectInfoPopover project={current} />}
						{current && (
							<Link
								to={`/projects/${current.id}/settings`}
								className="pill pill-icon !w-7 !h-7 text-[var(--color-text-dim)] hover:text-[var(--color-text)]"
								aria-label="Project settings"
								title="Settings"
							>
								<Settings className="w-3.5 h-3.5" />
							</Link>
						)}
					</div>

					{/* Center: view toggle (Graph / Jobs are peer views over the
					    same project data). Ask is an action, opened via ⌘K — not
					    a third view. */}
					<div className="flex items-center gap-1">
						<Pill
							onClick={() => current && navigate(`/projects/${current.id}/graph`)}
							className={view === "graph" ? "ring-1 ring-white/15" : "opacity-70 hover:opacity-100"}
							disabled={!current}
						>
							Graph
						</Pill>
						<Pill
							onClick={() => current && navigate(`/projects/${current.id}/jobs`)}
							className={view === "jobs" ? "ring-1 ring-white/15" : "opacity-70 hover:opacity-100"}
							disabled={!current}
						>
							Jobs
						</Pill>
					</div>

					{/* (settings page activates the gear icon on the left,
					    not a center toggle — so no third pill here) */}

					{/* Right: ask shortcut + account */}
					<div className="flex items-center gap-2">
						<button
							type="button"
							onClick={() => setPaletteOpen(true)}
							className="pill !px-2.5 !py-1.5 text-[var(--color-text-muted)] hover:text-[var(--color-text)] flex items-center gap-2"
							aria-label="Ask anything"
							title="Ask anything (⌘K)"
							disabled={!current}
						>
							<span className="text-[12px]">Ask</span>
							<kbd className="hidden sm:inline-flex items-center gap-[3px] px-1.5 py-0.5 rounded-[3px] bg-white/[0.05] border border-[var(--color-border)] text-[9.5px] mono text-[var(--color-text-dim)]">
								<span>⌘</span>
								<span>K</span>
							</kbd>
						</button>
						<AccountMenu
							userName={session.data?.user?.name}
							userImage={session.data?.user?.image}
							userRole={(session.data?.user as { role?: string })?.role}
						/>
					</div>
				</GlassPanel>
			</header>

			<main className="flex-1 relative min-h-0 px-4 pb-4">{children}</main>

			<AskPalette
				open={paletteOpen}
				onClose={closePalette}
				seed={paletteSeed}
			/>
		</Surface>
	);
}

interface OrgSwitcherProps {
	organizations: Organization[];
	currentId?: string;
	currentName?: string;
}

function OrgSwitcher({ organizations, currentId, currentName }: OrgSwitcherProps) {
	const qc = useQueryClient();
	const [selectOpen, setSelectOpen] = useState(false);

	const setActive = useMutation({
		mutationFn: async (orgId: string) => {
			const r = await authClient.organization.setActive({
				organizationId: orgId,
			});
			if (r.error) throw new Error(r.error.message ?? "set-active failed");
		},
		onSuccess: () => {
			// Refetch the session so activeOrganizationId updates, and
			// the projects list so the switcher repopulates against the
			// new active org.
			qc.invalidateQueries({ queryKey: ["session"] });
			qc.invalidateQueries({ queryKey: ["projects"] });
			// Better-auth's useSession hook reads from its own store;
			// trigger a refresh so the activeOrganizationId surfaces.
			authClient.getSession().catch(() => {});
		},
	});

	return (
		<Select.Root
			value={currentId}
			onValueChange={(id) => id && setActive.mutate(String(id))}
			open={selectOpen}
			onOpenChange={setSelectOpen}
		>
			<Select.Trigger className="pill !px-3">
				<span className="truncate max-w-[160px] text-[12px]">
					{currentName ?? "Workspace"}
				</span>
				<ChevronDown className="w-3.5 h-3.5 text-[var(--color-text-dim)]" />
			</Select.Trigger>
			<Select.Portal>
				<Select.Positioner sideOffset={6} align="start" className="z-50">
					<Select.Popup
						className="rounded-[12px] py-1 min-w-[220px] max-h-[60vh] overflow-y-auto"
						style={{
							background: "rgb(20 20 23 / 0.98)",
							backdropFilter: "blur(20px) saturate(180%)",
							WebkitBackdropFilter: "blur(20px) saturate(180%)",
							border: "1px solid rgb(255 255 255 / 0.1)",
							boxShadow:
								"0 12px 40px rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.06)",
						}}
					>
						<div className="px-3 pt-1.5 pb-1 text-[10px] uppercase tracking-wider text-[var(--color-text-dim)]">
							Workspaces
						</div>
						{organizations.map((o) => {
							const active = o.id === currentId;
							return (
								<Select.Item
									key={o.id}
									value={o.id}
									className={`flex items-center gap-2 mx-1 my-px px-3 py-1.5 rounded-[6px] text-[13px] outline-none cursor-pointer data-[highlighted]:bg-white/[0.06] ${
										active ? "bg-white/[0.04]" : ""
									}`}
								>
									<Select.ItemText className="truncate flex-1">
										{o.name}
									</Select.ItemText>
									{active && (
										<Check className="ml-auto w-3.5 h-3.5 text-[var(--color-text)] shrink-0" />
									)}
								</Select.Item>
							);
						})}
						{organizations.length === 0 && (
							<div className="px-3 py-2 text-[12px] text-[var(--color-text-muted)]">
								No workspaces.
							</div>
						)}
					</Select.Popup>
				</Select.Positioner>
			</Select.Portal>
		</Select.Root>
	);
}

interface ProjectSwitcherProps {
	projects: Project[];
	currentId?: string;
	currentName?: string;
	pinnedProjectIds?: string[];
	onSelect: (id: string) => void;
}

function ProjectSwitcher({
	projects,
	currentId,
	currentName,
	pinnedProjectIds = [],
	onSelect,
}: ProjectSwitcherProps) {
	const qc = useQueryClient();
	const [newOpen, setNewOpen] = useState(false);
	const [selectOpen, setSelectOpen] = useState(false);
	const pinnedSet = new Set(pinnedProjectIds);
	const updatePinnedProjects = useMutation({
		mutationFn: async (nextPinnedProjectIds: string[]) => {
			const r = await fetch("/api/settings", {
				method: "PUT",
				headers: { "Content-Type": "application/json" },
				credentials: "include",
				body: JSON.stringify({ pinnedProjectIds: nextPinnedProjectIds }),
			});
			if (!r.ok) throw new Error(`pin-project: ${r.status}`);
		},
		onSuccess: async () => {
			await qc.invalidateQueries({ queryKey: ["settings"] });
		},
	});
	// Toggle one project's pinned state, sending the resulting full set.
	const togglePin = (projectId: string) => {
		const next = pinnedSet.has(projectId)
			? pinnedProjectIds.filter((id) => id !== projectId)
			: [...pinnedProjectIds, projectId];
		updatePinnedProjects.mutate(next);
	};
	// Pinned ("favorite") projects float to the top; the rest sort
	// alphabetically.
	const orderedProjects = [...projects].sort((a, b) => {
		const ap = pinnedSet.has(a.id);
		const bp = pinnedSet.has(b.id);
		if (ap !== bp) return ap ? -1 : 1;
		return a.name.localeCompare(b.name);
	});
	return (
		<>
		<Select.Root
			value={currentId}
			onValueChange={(id) => id && onSelect(String(id))}
			open={selectOpen}
			onOpenChange={setSelectOpen}
		>
			<Select.Trigger className="pill !px-3">
				<span className="truncate max-w-[180px]">{currentName ?? "Select project"}</span>
				<ChevronDown className="w-3.5 h-3.5 text-[var(--color-text-dim)]" />
			</Select.Trigger>
			<Select.Portal>
				<Select.Positioner sideOffset={6} align="start" className="z-50">
					<Select.Popup
						className="rounded-[12px] py-1 min-w-[240px] max-h-[60vh] overflow-y-auto"
						style={{
							background: "rgb(20 20 23 / 0.98)",
							backdropFilter: "blur(20px) saturate(180%)",
							WebkitBackdropFilter: "blur(20px) saturate(180%)",
							border: "1px solid rgb(255 255 255 / 0.1)",
							boxShadow:
								"0 12px 40px rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.06)",
						}}
					>
						{orderedProjects.map((p) => {
							const active = p.id === currentId;
							const pinned = pinnedSet.has(p.id);
							return (
								<Select.Item
									key={p.id}
									value={p.id}
									className={`group flex items-center gap-2 mx-1 my-px px-3 py-1.5 rounded-[6px] text-[13px] outline-none cursor-pointer data-[highlighted]:bg-white/[0.06] ${
										active ? "bg-white/[0.04]" : ""
									}`}
								>
									<Select.ItemText className="truncate flex-1">{p.name}</Select.ItemText>
									<button
										type="button"
										onMouseDown={(e) => {
											e.preventDefault();
											e.stopPropagation();
										}}
										onClick={(e) => {
											e.preventDefault();
											e.stopPropagation();
											togglePin(p.id);
										}}
										// Pinned: filled pin, always visible. Unpinned: an
										// outline pin that only fades in on row hover/keyboard
										// focus, so the picker stays quiet until you reach for it.
										className={`inline-flex items-center justify-center rounded-[6px] p-1 transition-[color,opacity] ${
											pinned
												? "text-amber-300 hover:text-amber-200"
												: "text-[var(--color-text-dim)] hover:text-[var(--color-text)] opacity-0 focus-visible:opacity-100 group-hover:opacity-100 group-data-[highlighted]:opacity-100"
										}`}
										aria-label={
											pinned ? `Unpin ${p.name}` : `Pin ${p.name}`
										}
										title={pinned ? "Pinned" : "Pin project"}
										disabled={updatePinnedProjects.isPending}
									>
										<Pin
											className={`w-3.5 h-3.5 shrink-0 ${pinned ? "fill-current" : ""}`}
										/>
									</button>
									{active && (
										<Check className="ml-auto w-3.5 h-3.5 text-[var(--color-text)] shrink-0" />
									)}
								</Select.Item>
							);
						})}
						<div className="border-t border-[var(--color-border)] mt-1 pt-1 mx-1">
							<button
								type="button"
								onClick={() => {
									// Close the Select first so its focus/keyboard
									// trap releases — otherwise it eats keystrokes
									// inside the dialog's text input.
									setSelectOpen(false);
									setNewOpen(true);
								}}
								className="w-full flex items-center gap-2 px-3 py-1.5 rounded-[6px] text-[13px] text-[var(--color-text-muted)] hover:text-[var(--color-text)] hover:bg-white/[0.06] cursor-pointer outline-none transition-colors"
							>
								<Plus className="w-3.5 h-3.5" />
								New project
							</button>
						</div>
					</Select.Popup>
				</Select.Positioner>
			</Select.Portal>
		</Select.Root>
		<NewProjectDialog open={newOpen} onOpenChange={setNewOpen} />
		</>
	);
}

interface ProjectInfoPopoverProps {
	project: Project;
}

function ProjectInfoPopover({ project }: ProjectInfoPopoverProps) {
	const [copied, setCopied] = useState<"id" | "snippet" | null>(null);

	const copyText = async (text: string, key: "id" | "snippet") => {
		try {
			await navigator.clipboard.writeText(text);
			setCopied(key);
			setTimeout(() => setCopied(null), 1200);
		} catch {
			// clipboard blocked — silent
		}
	};

	const cliSnippet = `bun add -g @codespring-app/v3-cli\nv3 auth login\nv3 project use ${project.id}`;

	return (
		<Popover.Root>
			<Popover.Trigger
				className="pill pill-icon !w-7 !h-7 text-[var(--color-text-dim)] hover:text-[var(--color-text)]"
				aria-label="Project details"
			>
				<Info className="w-3.5 h-3.5" />
			</Popover.Trigger>
			<Popover.Portal>
				<Popover.Positioner sideOffset={6} align="start" className="z-50">
					<Popover.Popup
						className="rounded-[12px] p-3 min-w-[320px] max-w-[380px] space-y-2"
						style={{
							background: "rgb(20 20 23 / 0.98)",
							backdropFilter: "blur(20px) saturate(180%)",
							WebkitBackdropFilter: "blur(20px) saturate(180%)",
							border: "1px solid rgb(255 255 255 / 0.1)",
							boxShadow:
								"0 12px 40px rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.06)",
						}}
					>
						<InfoRow label="name" value={project.name} />
						<InfoRow
							label="id"
							value={project.id}
							mono
							action={
								<button
									type="button"
									onClick={() => copyText(project.id, "id")}
									className="text-[var(--color-text-dim)] hover:text-[var(--color-text)] transition-colors"
									aria-label="Copy id"
								>
									<Copy className="w-3 h-3" />
								</button>
							}
							hint={copied === "id" ? "copied" : undefined}
						/>
						{project.githubRepo && (
							<InfoRow label="github" value={project.githubRepo} mono />
						)}
						{project.defaultBranch && (
							<InfoRow label="branch" value={project.defaultBranch} mono />
						)}
						{project.createdAt && (
							<InfoRow
								label="created"
								value={new Date(project.createdAt).toLocaleString()}
							/>
						)}

						<div className="border-t border-[var(--color-border)] my-2" />

						<div>
							<div className="flex items-center justify-between mb-1.5">
								<div className="text-[10px] uppercase tracking-wider text-[var(--color-text-dim)]">
									Use with CLI
								</div>
								<button
									type="button"
									onClick={() => copyText(cliSnippet, "snippet")}
									className="text-[10px] text-[var(--color-text-dim)] hover:text-[var(--color-text)] transition-colors flex items-center gap-1"
									aria-label="Copy commands"
								>
									{copied === "snippet" ? "copied" : <><Copy className="w-3 h-3" /> copy</>}
								</button>
							</div>
							<pre className="mono text-[11px] text-[var(--color-text-muted)] bg-black/30 border border-[var(--color-border)] rounded-md p-2 whitespace-pre overflow-x-auto leading-relaxed">
								<span className="text-[var(--color-text-dim)]">$ </span>bun add -g @codespring-app/v3-cli{"\n"}
								<span className="text-[var(--color-text-dim)]">$ </span>v3 auth login{"\n"}
								<span className="text-[var(--color-text-dim)]">$ </span>v3 project use <span className="text-[var(--color-text)]">{project.id}</span>
							</pre>
							<a
								href="https://www.npmjs.com/package/@codespring-app/v3-cli"
								target="_blank"
								rel="noreferrer"
								className="mt-1.5 inline-flex items-center gap-1 text-[11px] text-[var(--color-text-dim)] hover:text-[var(--color-text)] transition-colors"
							>
								<ExternalLink className="w-3 h-3" />
								view on npmjs
							</a>
						</div>
					</Popover.Popup>
				</Popover.Positioner>
			</Popover.Portal>
		</Popover.Root>
	);
}

interface InfoRowProps {
	label: string;
	value: string;
	mono?: boolean;
	action?: React.ReactNode;
	hint?: string;
}

function InfoRow({ label, value, mono, action, hint }: InfoRowProps) {
	return (
		<div>
			<div className="flex items-center justify-between">
				<div className="text-[10px] uppercase tracking-wider text-[var(--color-text-dim)]">
					{label}
				</div>
				{hint ? (
					<div className="text-[10px] text-[var(--color-text-muted)]">{hint}</div>
				) : (
					action
				)}
			</div>
			<div className={`text-[12px] truncate ${mono ? "mono" : ""}`}>{value}</div>
		</div>
	);
}

interface AccountMenuProps {
	userName?: string;
	userImage?: string | null;
	userRole?: string;
}

function AccountMenu({ userName, userImage, userRole }: AccountMenuProps) {
	const navigate = useNavigate();
	return (
		<Popover.Root>
			<Popover.Trigger className="pill pill-icon !p-0 overflow-hidden">
				{userImage ? (
					<img src={userImage} alt={userName ?? ""} className="w-full h-full object-cover" />
				) : (
					<User className="w-3.5 h-3.5" />
				)}
			</Popover.Trigger>
			<Popover.Portal>
				<Popover.Positioner sideOffset={8} align="end" className="z-50">
					<Popover.Popup
						className="rounded-[12px] p-1 min-w-[220px]"
						style={{
							background: "rgb(20 20 23 / 0.98)",
							backdropFilter: "blur(20px) saturate(180%)",
							WebkitBackdropFilter: "blur(20px) saturate(180%)",
							border: "1px solid rgb(255 255 255 / 0.1)",
							boxShadow:
								"0 12px 40px rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.06)",
						}}
					>
						<div className="px-3 py-2 border-b border-[var(--color-border)] mb-1">
							<div className="text-[10px] uppercase tracking-wider text-[var(--color-text-dim)]">
								Signed in as
							</div>
							<div className="text-[13px] truncate">{userName ?? "—"}</div>
						</div>
						{userRole === "admin" && (
							<button
								type="button"
								onClick={() => navigate("/admin")}
								className="w-full flex items-center gap-2 px-3 py-2 text-[13px] text-[var(--color-text-muted)] hover:text-[var(--color-text)] hover:bg-white/[0.06] rounded-[6px] transition-colors"
							>
								<Shield className="w-3.5 h-3.5" />
								Admin
							</button>
						)}
						<button
							type="button"
							onClick={() => navigate("/account/usage")}
							className="w-full flex items-center gap-2 px-3 py-2 text-[13px] text-[var(--color-text-muted)] hover:text-[var(--color-text)] hover:bg-white/[0.06] rounded-[6px] transition-colors"
						>
							<BarChart3 className="w-3.5 h-3.5" />
							Usage
						</button>
						<button
							type="button"
							onClick={() => navigate("/account/settings")}
							className="w-full flex items-center gap-2 px-3 py-2 text-[13px] text-[var(--color-text-muted)] hover:text-[var(--color-text)] hover:bg-white/[0.06] rounded-[6px] transition-colors"
						>
							<Settings className="w-3.5 h-3.5" />
							Settings
						</button>
						<button
							type="button"
							onClick={() => signOut()}
							className="w-full flex items-center gap-2 px-3 py-2 text-[13px] text-[var(--color-text-muted)] hover:text-[var(--color-text)] hover:bg-white/[0.06] rounded-[6px] transition-colors"
						>
							<LogOut className="w-3.5 h-3.5" />
							Sign out
						</button>
					</Popover.Popup>
				</Popover.Positioner>
			</Popover.Portal>
		</Popover.Root>
	);
}
